import { useState, useRef, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { callAI, ASHU_SYSTEM_PROMPT } from '@/lib/ai'
import { MessageCircle, X, Send } from 'lucide-react'

interface Message {
  role: 'user' | 'assistant'
  content: string
}

export function ChatWidget() {
  const [isOpen, setIsOpen] = useState(false)
  const [messages, setMessages] = useState<Message[]>([
    {
      role: 'assistant',
      content: 'Hi! I\'m Ashu AI 👋 How can I help you with your cleaning needs today? Call +251 99 774 5317 to book!'
    }
  ])
  const [input, setInput] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const scrollRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight
    }
  }, [messages])

  const handleSend = async () => {
    if (!input.trim() || isLoading) return

    const userMsg = input.trim()
    setInput('')
    setMessages((prev) => [...prev, { role: 'user', content: userMsg }])
    setIsLoading(true)

    const history = messages.map((m) => ({ role: m.role, content: m.content }))
    const response = await callAI(ASHU_SYSTEM_PROMPT, [...history, { role: 'user', content: userMsg }])

    setMessages((prev) => [...prev, { role: 'assistant', content: response }])
    setIsLoading(false)
  }

  return (
    <>
      {/* Chat Button */}
      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        style={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          width: 60,
          height: 60,
          borderRadius: '50%',
          background: 'var(--lime)',
          border: 'none',
          cursor: 'pointer',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 4px 20px rgba(232,255,0,0.3)'
        }}
      >
        <AnimatePresence mode="wait">
          {isOpen ? (
            <motion.div
              key="close"
              initial={{ rotate: -90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: 90, opacity: 0 }}
            >
              <X size={24} color="var(--black)" />
            </motion.div>
          ) : (
            <motion.div
              key="open"
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0, opacity: 0 }}
            >
              <MessageCircle size={24} color="var(--black)" />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Pulse rings */}
        {!isOpen && (
          <>
            <motion.div
              style={{
                position: 'absolute',
                inset: -4,
                borderRadius: '50%',
                border: '2px solid var(--lime)'
              }}
              animate={{ scale: [1, 1.2, 1], opacity: [0.5, 0, 0.5] }}
              transition={{ duration: 2, repeat: Infinity }}
            />
            <motion.div
              style={{
                position: 'absolute',
                inset: -8,
                borderRadius: '50%',
                border: '1px solid var(--lime)'
              }}
              animate={{ scale: [1, 1.3, 1], opacity: [0.3, 0, 0.3] }}
              transition={{ duration: 2, repeat: Infinity, delay: 0.5 }}
            />
          </>
        )}
      </motion.button>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.9 }}
            transition={{ type: 'spring', stiffness: 300, damping: 25 }}
            style={{
              position: 'fixed',
              bottom: 100,
              right: 24,
              width: 360,
              maxWidth: 'calc(100vw - 48px)',
              height: 500,
              maxHeight: 'calc(100vh - 140px)',
              background: 'var(--surface)',
              border: '1px solid rgba(232,255,0,0.15)',
              borderRadius: 20,
              zIndex: 999,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              boxShadow: '0 20px 60px rgba(0,0,0,0.5)'
            }}
          >
            {/* Header */}
            <div
              style={{
                padding: '16px 20px',
                background: 'var(--lime)',
                display: 'flex',
                alignItems: 'center',
                gap: 12
              }}
            >
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  background: 'var(--black)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.2rem'
                }}
              >
                🧹
              </div>
              <div>
                <div
                  style={{
                    fontFamily: 'DM Sans',
                    fontWeight: 700,
                    fontSize: '0.95rem',
                    color: 'var(--black)'
                  }}
                >
                  Ashu Assistant
                </div>
                <div
                  style={{
                    fontFamily: 'DM Sans',
                    fontSize: '0.75rem',
                    color: 'var(--black)',
                    opacity: 0.7
                  }}
                >
                  AI Powered · Online
                </div>
              </div>
            </div>

            {/* Messages */}
            <div
              ref={scrollRef}
              style={{
                flex: 1,
                overflow: 'auto',
                padding: 20,
                display: 'flex',
                flexDirection: 'column',
                gap: 12
              }}
            >
              {messages.map((msg, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  style={{
                    alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start',
                    maxWidth: '85%',
                    padding: '12px 16px',
                    borderRadius: 16,
                    background: msg.role === 'user' ? 'var(--lime)' : 'var(--elevated)',
                    color: msg.role === 'user' ? 'var(--black)' : 'var(--white)',
                    fontFamily: 'DM Sans',
                    fontSize: '0.9rem',
                    lineHeight: 1.5,
                    borderBottomLeftRadius: msg.role === 'user' ? 16 : 4,
                    borderBottomRightRadius: msg.role === 'user' ? 4 : 16
                  }}
                >
                  {msg.content}
                </motion.div>
              ))}
              {isLoading && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  style={{
                    alignSelf: 'flex-start',
                    padding: '12px 16px',
                    borderRadius: 16,
                    background: 'var(--elevated)',
                    borderBottomLeftRadius: 4
                  }}
                >
                  <motion.span
                    animate={{ opacity: [0.4, 1, 0.4] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                    style={{ color: 'var(--white-60)', fontSize: '0.9rem' }}
                  >
                    Typing...
                  </motion.span>
                </motion.div>
              )}
            </div>

            {/* Input */}
            <div
              style={{
                padding: 16,
                borderTop: '1px solid rgba(255,255,255,0.06)',
                display: 'flex',
                gap: 12
              }}
            >
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                placeholder="Type your message..."
                style={{
                  flex: 1,
                  padding: '12px 16px',
                  background: 'var(--elevated)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 12,
                  color: 'var(--white)',
                  fontFamily: 'DM Sans',
                  fontSize: '0.9rem',
                  outline: 'none'
                }}
              />
              <motion.button
                onClick={handleSend}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                disabled={isLoading || !input.trim()}
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 12,
                  background: 'var(--lime)',
                  border: 'none',
                  cursor: input.trim() ? 'pointer' : 'not-allowed',
                  opacity: input.trim() ? 1 : 0.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Send size={18} color="var(--black)" />
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}
